import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const StoryReader = ({ story, formToken }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customChoice, setCustomChoice] = useState('');
  const [currentStory, setCurrentStory] = useState(story);

  // Handle a choice selection
  const handleChoiceSelection = async (choiceIndex) => {
    setIsLoading(true);

    try {
      const response = await axios.post(
        `/adventure_stories/${currentStory.id}/choose_path`,
        {
          choice_index: choiceIndex,
          current_page: currentStory.current_page
        },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      setIsLoading(false);
      setCurrentStory(response.data);
    } catch (error) {
      console.error("Error selecting choice:", error);
      setIsLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  // Handle a custom choice submission
  const handleCustomChoice = async () => {
    if (!customChoice.trim()) return;

    setIsLoading(true);

    try {
      const response = await axios.post(
        `/adventure_stories/${currentStory.id}/choose_path`,
        {
          custom_choice: customChoice,
          current_page: currentStory.current_page
        },
        {
          headers: {
            "X-CSRF-Token": formToken,
            "Content-Type": "application/json"
          }
        }
      );

      setIsLoading(false);
      setShowCustomInput(false);
      setCustomChoice('');
      setCurrentStory(response.data);
    } catch (error) {
      console.error("Error submitting custom choice:", error);
      setIsLoading(false);
      alert("Something went wrong. Please try again.");
    }
  };

  // Show the cover page
  if (currentStory.current_page === 0) {
    return (
      <div className="max-w-4xl mx-auto flex flex-col items-center">
        <div className="bg-white rounded-xl shadow-xl overflow-hidden w-full max-w-2xl">
          {currentStory.cover.image_url && (
            <img
              src={currentStory.cover.image_url}
              alt={currentStory.cover.title}
              className="w-full object-cover h-96"
            />
          )}
          <div className="p-8 text-center">
            <h1 className="text-4xl font-bold mb-3 text-indigo-900">{currentStory.cover.title}</h1>
            <p className="text-xl italic text-gray-600 mb-8">{currentStory.cover.subtitle}</p>

            {isLoading ? (
              <div className="flex justify-center items-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
                <p className="ml-3 text-indigo-700">Opening book...</p>
              </div>
            ) : (
              <button
                onClick={() => handleChoiceSelection('start')}
                className="px-8 py-3 bg-indigo-600 text-white rounded-lg text-xl font-semibold hover:bg-indigo-700 transition-colors"
              >
                Begin Your Adventure
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Get the current page content
  const currentPageIndex = currentStory.current_page - 1;
  const currentPage = currentStory.pages[currentPageIndex];

  if (!currentPage) {
    return (
      <div className="max-w-4xl mx-auto p-8 bg-white rounded-xl shadow-lg">
        <h2 className="text-xl font-bold text-red-500">Page not found</h2>
        <p className="mt-2">We couldn't find this page in your story.</p>
        <a
          href="/adventure_stories"
          className="mt-4 inline-block px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600"
        >
          Start a New Adventure
        </a>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-xl overflow-hidden mb-8">
        <div className="bg-indigo-100 px-6 py-3 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-indigo-800">Page {currentStory.current_page}</h2>
          <a
            href="/adventure_stories"
            className="text-indigo-600 hover:text-indigo-800"
          >
            Choose New Story
          </a>
        </div>

        {currentPage.image_url ? (
          <img
            src={currentPage.image_url}
            alt={`Illustration for page ${currentStory.current_page}`}
            className="w-full object-cover h-80"
          />
        ) : (
          <div className="h-32 bg-indigo-50 flex items-center justify-center">
            <p className="text-indigo-400">Image is being created...</p>
          </div>
        )}

        <div className="p-8">
          <p className="text-lg leading-relaxed mb-8">{currentPage.content}</p>

          {currentPage.choices && currentPage.choices.length > 0 && !isLoading && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-indigo-800">Choose Your Next Adventure</h3>

              <div className="space-y-3">
                {currentPage.choices.map((choice, index) => (
                  <button
                    key={index}
                    onClick={() => handleChoiceSelection(index)}
                    className="w-full text-left p-4 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition"
                  >
                    {choice}
                  </button>
                ))}

                {!showCustomInput ? (
                  <button
                    onClick={() => setShowCustomInput(true)}
                    className="w-full text-left p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition flex items-center"
                  >
                    <span className="mr-2">✍️</span> Write your own path...
                  </button>
                ) : (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <textarea
                      value={customChoice}
                      onChange={(e) => setCustomChoice(e.target.value)}
                      placeholder="Write your own adventure direction..."
                      className="w-full p-3 border rounded-md focus:ring-2 focus:ring-indigo-500"
                      rows={3}
                    />
                    <div className="flex justify-end mt-2 space-x-2">
                      <button
                        onClick={() => setShowCustomInput(false)}
                        className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCustomChoice}
                        className="px-3 py-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
              <p className="ml-3 text-lg text-indigo-700">Creating the next part of your adventure...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

StoryReader.propTypes = {
  story: PropTypes.shape({
    id: PropTypes.number.isRequired,
    current_page: PropTypes.number.isRequired,
    cover: PropTypes.shape({
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      image_url: PropTypes.string
    }),
    pages: PropTypes.arrayOf(
      PropTypes.shape({
        page_number: PropTypes.number,
        content: PropTypes.string,
        image_url: PropTypes.string,
        choices: PropTypes.arrayOf(PropTypes.string)
      })
    )
  }).isRequired,
  formToken: PropTypes.string.isRequired
};

export default StoryReader;